/**
 * Creates a throttled function that limits the number of invocations
 * within a specified time period. Useful for rate limiting API calls
 * or user interactions like login attempts.
 * 
 * @param key A unique identifier for the throttle instance (e.g., user IP or email)
 * @param maxAttempts Maximum number of attempts allowed in the time window
 * @param timeWindowMs Time window in milliseconds
 * @returns An object with check and reset methods
 */
interface ThrottleState {
  attempts: number;
  firstAttemptTime: number;
  blockedUntil: number | null;
}

interface ThrottleResult {
  blocked: boolean;
  remainingAttempts: number;
  blockedUntil: number | null;
}

export function createThrottle(key: string, maxAttempts: number, timeWindowMs: number) {
  const state: ThrottleState = {
    attempts: 0,
    firstAttemptTime: Date.now(),
    blockedUntil: null
  };

  const check = (): ThrottleResult => {
    const now = Date.now();

    // If blocked, check if block has expired
    if (state.blockedUntil !== null) {
      if (now >= state.blockedUntil) {
        // Reset state if block has expired
        state.attempts = 0;
        state.firstAttemptTime = now;
        state.blockedUntil = null;
      } else {
        return {
          blocked: true,
          remainingAttempts: 0,
          blockedUntil: state.blockedUntil
        };
      }
    }

    // Check if time window has expired
    if (now - state.firstAttemptTime >= timeWindowMs) {
      // Reset attempts if time window has expired
      state.attempts = 0;
      state.firstAttemptTime = now;
    }

    return {
      blocked: false,
      remainingAttempts: Math.max(0, maxAttempts - state.attempts),
      blockedUntil: null
    };
  };

  const increment = (): ThrottleResult => {
    const now = Date.now();
    state.attempts++;

    if (state.attempts >= maxAttempts) {
      // Block for the remainder of the time window plus an additional window
      const remainingTime = timeWindowMs - (now - state.firstAttemptTime);
      state.blockedUntil = now + remainingTime + timeWindowMs;
      
      return {
        blocked: true,
        remainingAttempts: 0,
        blockedUntil: state.blockedUntil
      };
    }

    return {
      blocked: false,
      remainingAttempts: Math.max(0, maxAttempts - state.attempts),
      blockedUntil: null
    };
  };

  const reset = () => {
    state.attempts = 0;
    state.firstAttemptTime = Date.now();
    state.blockedUntil = null;
  };

  return {
    check,
    increment,
    reset
  };
}

// Store to track throttle data across the application
interface ThrottleData {
  attempts: number;
  firstAttemptTime: number;
  blocked: boolean;
  blockExpiry: number;
}

interface ThrottleStore {
  [key: string]: ThrottleData;
} 